import { useId, useState } from "react";

import { errorMessage, useVerifySecondFactor } from "../api/queries";
import { Dialog } from "../components/ui/Dialog";
import { Field } from "../components/ui/Field";

interface StepUpDialogProps {
  /**
   * Whether there is a code to prove against.
   *
   * The refusal carries `details.enrolled`; when it is false there is nothing
   * to type and the dialog says where to set one up instead of showing a box
   * that could never be right.
   */
  enrolled: boolean;
  /** What the server said when it asked for the code, rendered verbatim. */
  serverMessage: string;
  /** Called once a code was accepted; the act is replayed from here. */
  onConfirmed: () => void;
  onClose: () => void;
}

/**
 * "Confirm with your code" — the prompt in front of the four gated acts.
 *
 * Widening a consent, asking for a file to be destroyed, approving an account
 * and minting a key each answer `403 step_up_required` until a code has been
 * confirmed. This asks for the six digits and then repeats the act that was
 * refused, so nobody has to fill a form in twice.
 *
 * It is never called MFA on screen. What the console runs is its own code, and
 * the word on the button is the word in every other sentence about it.
 *
 * Args:
 *     enrolled: Whether the account has a code at all.
 *     serverMessage: The server's own sentence about the refusal.
 *     onConfirmed: Replay the act.
 *     onClose: Give up on it.
 *
 * Returns:
 *     The dialog.
 */
export function StepUpDialog({
  enrolled,
  serverMessage,
  onConfirmed,
  onClose,
}: StepUpDialogProps): JSX.Element {
  const codeId = useId();
  const [code, setCode] = useState("");
  const verify = useVerifySecondFactor();

  if (!enrolled) {
    return (
      <Dialog
        title="Confirm with your code"
        onClose={onClose}
        footer={
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        }
      >
        <p className="dialog__text">{serverMessage}</p>
        <p className="dialog__text">
          There is no code on this account yet. Open your Account page and set
          one up — it takes about a minute, and then this act goes through.
        </p>
      </Dialog>
    );
  }

  const submit = (): void => {
    verify.mutate(
      { code },
      {
        onSuccess: () => {
          setCode("");
          onConfirmed();
        },
      },
    );
  };

  return (
    <Dialog
      title="Confirm with your code"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={verify.isPending || code.trim().length === 0}
            onClick={submit}
          >
            {verify.isPending ? "Checking…" : "Confirm"}
          </button>
        </>
      }
    >
      <p className="dialog__text">{serverMessage}</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field
          label="The six digits from your phone"
          htmlFor={codeId}
          hint="Spaces do not matter, and a recovery code works here too."
          error={verify.isError ? errorMessage(verify.error) : undefined}
        >
          <input
            id={codeId}
            className="input input--mono input--narrow"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
          />
        </Field>
      </form>
    </Dialog>
  );
}
